<template>
  <div class="single-article-modules">
    <template
      v-for="(module, index) in articleModules"
      :key="`${module.acf_fc_layout}-${index}`">
      <component
        :is="articleModuleComponents[module.acf_fc_layout]"
        v-if="articleModuleComponents[module.acf_fc_layout]"
        v-bind="articleModuleProps(module, index)" />
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/stores/main'
import useUtils from '@/components/composables/useUtils.js'
import ArticleTextComponent from '@/components/article-modules/ArticleTextComponent.vue'
import ArticleHeroComponent from '@/components/article-modules/ArticleHeroComponent.vue'
import ArticleTitleWithImageComponent from '@/components/article-modules/ArticleTitleWithImageComponent.vue'
import ArticleMediaComponent from '@/components/article-modules/ArticleMediaComponent.vue'
import ArticleRelatedArticlesComponent from '@/components/article-modules/ArticleRelatedArticlesComponent.vue'
import ArticleBackComponent from '@/components/article-modules/ArticleBackComponent.vue'
import ArticleOverlayedImagesComponent from '@/components/article-modules/ArticleOverlayedImagesComponent.vue'
import ArticleQuoteComponent from '@/components/article-modules/ArticleQuoteComponent.vue'
import ArticleInThisSeriesComponent from '@/components/article-modules/ArticleInThisSeriesComponent.vue'
import SpacerComponent from '@/components/modules/SpacerComponent.vue'

const props = defineProps({
  slug: {
    type: String,
    required: true,
  },
})

const router = useRouter()
const store = useStore()
const { decodeTitle } = useUtils()

const articleModuleComponents = {
  ArticleTextComponent,
  ArticleHeroComponent,
  ArticleTitleWithImageComponent,
  ArticleMediaComponent,
  ArticleRelatedArticlesComponent,
  ArticleBackComponent,
  ArticleOverlayedImagesComponent,
  ArticleQuoteComponent,
  ArticleInThisSeriesComponent,
  SpacerComponent,
}

const request = computed(() => ({
  type: 'articles',
  slug: props.slug,
  showLoading: true,
  force: true,
}))

const article = computed(() => store.getCurrentSingleBySlug(request.value))

const articleModules = computed(() => article.value?.acf?.article_modules || [])

const articleModuleProps = (module, index) => ({
  data: module,
  ...([
    'ArticleTitleWithImageComponent',
    'ArticleRelatedArticlesComponent',
  ].includes(module.acf_fc_layout)
    ? { post: article.value }
    : {}),
  ...(module.acf_fc_layout === 'ArticleInThisSeriesComponent'
    ? {
        noMediaBefore:
          articleModules.value[index - 1]?.acf_fc_layout !==
          'ArticleMediaComponent',
      }
    : {}),
  ...(module.acf_fc_layout === 'ArticleTitleWithImageComponent'
    ? {
        noMediaAfter:
          articleModules.value[index + 1]?.acf_fc_layout !==
          'ArticleMediaComponent',
      }
    : {}),
})

const loadArticle = async () => {
  await store.getSingleBySlug(request.value)

  if (!article.value) {
    router.replace('/404')
    return
  }

  store.updateDocTitle({
    parts: [decodeTitle(article.value.title.rendered), store.site.name],
  })
}

watch(() => props.slug, loadArticle, { immediate: true })

onMounted(() => {
  document.body.classList.add('single-article-white-bg')
})

onBeforeUnmount(() => {
  document.body.classList.remove('single-article-white-bg')
})
</script>

<style lang="scss" scoped>
.single-article {
  .single-article-header {
    @apply inner-container py-10 lg:py-20;

    h1 {
      @apply header-1 text-balanced;
    }
  }
}
</style>

<style lang="scss">
body.single-article-white-bg {
  background: #fff !important;
}
</style>
